import { fg } from '@atlaskit/platform-feature-flags';
import type { BackendExperiment } from '@atlassian/search-experiment';

import { type FireAnalyticsEvent } from '../../../common/types';
import { ALL_PRODUCTS_STRING, type QueryState } from '../../constants/analytics';
import { is3pProductKeyForAnalytics, type ProductKeys } from '../../constants/products';

import {
	type CommonAttributes,
	type NonPrivacySafeAttributes,
	type ResultShown,
	type SearchAndResultAttributes,
	type SocialSignalAttributes,
} from './types';

type AdvancedResultsShownAnalyticsProps = {
	queryState: QueryState;
	numResults: number | undefined;
	shownCount: number;
	are3pResultsShown: boolean;
	commonAttributes: CommonAttributes;
	nonPrivacySafeAttributes: NonPrivacySafeAttributes;
	searchAndResult: SearchAndResultAttributes;
	resultsShown: ResultShown[];
	socialSignalsAttributes: SocialSignalAttributes;
	requestedBackendExperiments: { backendExperiments: BackendExperiment };
	referrer: string | undefined;
	/** Whether the query used to search for these results was paginated.
	 * i.e. will be false for the first page of results, and true for subsequent pages.
	 */
	isPaginatedQuery: boolean;
	isBulkConnectBannerShown: boolean;
	productsWithTimeout: ProductKeys[] | undefined;
};

export const onAdvancedSearchResultsShown = ({
	queryState,
	numResults,
	shownCount,
	are3pResultsShown,
	commonAttributes,
	nonPrivacySafeAttributes,
	searchAndResult,
	resultsShown,
	socialSignalsAttributes,
	requestedBackendExperiments,
	referrer,
	isPaginatedQuery,
	isBulkConnectBannerShown,
	productsWithTimeout,
}: AdvancedResultsShownAnalyticsProps): FireAnalyticsEvent => ({
	eventType: 'track',
	action: 'shown',
	actionSubject: 'advancedSearchResults',
	attributes: {
		additionalAttributes: {
			queryState,
			numResults,
			numberOfResultsShown: shownCount,
			noResultsFetched: numResults === 0,
			numberOfDocs: numResults,
			are3pResultsShown,
			searchAndResult,
			resultsShown,
			is3PFilter:
				commonAttributes.productSelected !== ALL_PRODUCTS_STRING &&
				is3pProductKeyForAnalytics(commonAttributes.productSelected),
			isPaginatedQuery,
			isBulkConnectBannerShown,
			referrer,
			...socialSignalsAttributes,
			...(fg('relay_results_analytics_refactor')
				? {
						backendExperiments: {
							experimentLayers:
								requestedBackendExperiments.backendExperiments?.experimentLayers?.map(
									({ analyticsDefinitions, ...layer }) => {
										return {
											...layer,
											definitions: analyticsDefinitions,
										};
									},
								),
						},
					}
				: requestedBackendExperiments),
			productsWithTimeout,
		},
		commonAttributes,
	},
	nonPrivacySafeAttributes,
});
